import { useState } from "react";
import {
  GroupTextField,
  GroupTextFieldRow,
  Button,
  Input,
  Icon,
} from "@elaniintc/goes-design-system";
import type { RecipientEntry } from "@/schemas/workflow/notification-node";
import type { NodeVariable } from "../../types";
import { useRecipientInput } from "../hooks/useRecipientInput";
import { VariableSelector } from "./variable-selector";
import {
  isValidEmailInput,
  isValidEmail,
  isValidPhone,
} from "@/utils/validation";

interface RecipientInputProps {
  type: "email" | "phone";
  variables: NodeVariable[];
  elements: RecipientEntry[];
  onChange: (elements: RecipientEntry[]) => void;
}

/**
 * Componente genérico para renderizar inputs dinámicos de destinatarios
 * Soporta tanto emails como teléfonos con validaciones específicas
 * Permite tanto inputs manuales como selección de variables
 */
export const RecipientInput: React.FC<RecipientInputProps> = ({
  type,
  variables,
  elements,
  onChange,
}) => {
  const {
    updateElement,
    removeElement,
    addElement,
    toggleElementType,
    canAddMoreElements,
  } = useRecipientInput({ elements, onChange });

  // Track validation errors per field index
  const [errors, setErrors] = useState<Record<number, string>>({});

  const config = {
    email: {
      placeholder: "Escribe una dirección de correo",
      maxLength: 255,
      buttonText: "Añadir correo",
      validateInput: isValidEmailInput, // Permissive validation while typing
      validateBlur: (value: string) =>
        isValidEmail(value, { allowEmpty: false }), // Strict validation on blur
      errorMessage: "Formato de correo electrónico inválido",
    },
    phone: {
      placeholder: "+503 0000 0000",
      maxLength: 20,
      buttonText: "Añadir teléfono",
      validateInput: isValidPhone,
      validateBlur: (value: string) =>
        isValidPhone(value, { allowEmpty: false }),
      errorMessage: "Formato de teléfono inválido",
    },
  }[type];

  return (
    <div className="space-y-2">
      {elements.map((element, index) => {
        return (
          <div key={index} className="flex items-center gap-2">
            {element.type === "var" ? (
              <GroupTextField className="flex-1" label="">
                <GroupTextFieldRow className="overflow-x-hidden">
                  <Button
                    variant="quiet"
                    group="up-left"
                    onClick={() => toggleElementType(String(index))}
                  >
                    {element.type}
                  </Button>
                  <VariableSelector
                    variables={variables}
                    selectedData={{
                      value: element.value,
                      connection: element.connection,
                      field: element.field,
                      global_type: element.global_type,
                      output_name: element.output_name,
                    }}
                    type="STRING"
                    onUpdateElement={(data) => {
                      return updateElement(String(index), data);
                    }}
                  />
                </GroupTextFieldRow>
              </GroupTextField>
            ) : (
              <div className="flex-1 flex flex-col gap-1">
                <GroupTextField className="flex-1" label="">
                  <GroupTextFieldRow className="overflow-x-hidden">
                    <Button
                      variant="quiet"
                      onClick={() => toggleElementType(String(index))}
                    >
                      {element.type}
                    </Button>
                    <Input
                      className="w-full"
                      placeholder={config.placeholder}
                      maxLength={config.maxLength}
                      value={element.value}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        // Use permissive validation while typing
                        if (!config.validateInput(newValue)) return;

                        // Clear error when user starts typing again
                        if (errors[index]) {
                          setErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors[index];
                            return newErrors;
                          });
                        }

                        updateElement(String(index), newValue);
                      }}
                      onBlur={(e) => {
                        const value = e.target.value.trim();
                        // Validate with strict rules when user leaves the field
                        if (value && !config.validateBlur(value)) {
                          setErrors((prev) => ({
                            ...prev,
                            [index]: config.errorMessage,
                          }));
                        }
                      }}
                    />
                  </GroupTextFieldRow>
                </GroupTextField>
                {errors[index] && (
                  <span className="text-danger text-xs leading-xl font-normal">
                    {errors[index]}
                  </span>
                )}
              </div>
            )}
            {index > 0 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeElement(String(index))}
                aria-label="Eliminar"
              >
                <Icon name="times" type="far" />
              </Button>
            )}
          </div>
        );
      })}

      {elements.length < 10 && (
        <div className="flex gap-2">
          <Button
            variant="tertiary"
            size="sm"
            onClick={addElement}
            disabled={!canAddMoreElements}
          >
            {config.buttonText} <Icon name="plus" type="far" />
          </Button>
        </div>
      )}
    </div>
  );
};
